import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Container, Typography, Grid, Button, Snackbar, Alert, Box, CircularProgress } from '@mui/material';
import { BatchProgress } from '../../components/BatchProgress/BatchProgress';
import apiService from '../../services/apiService';
import batchService from '../../services/batchService';
import ErrorToast from '../../components/ErrorToast/ErrorToast';
import RateLimitInfo from '../../components/RateLimitInfo';

const BatchJobCard = memo((
  { name, progress, isActive, startTime, batchName, statistics, onStart, onStop, loading, obtainBirthDates, setObtainBirthDates, lastJobObtainBirthDates }
) => {
  const isCompleted = progress === 100;
  const isBPS = name.toLowerCase() === 'bps';

  return (
    <Box 
      sx={{ 
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(27, 40, 94, 0.08)',
        p: 3,
        mb: name.toLowerCase() === 'apollo' ? 3 : 0
      }}
    >
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={9}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2,
              color: '#1B285E',
              fontFamily: '"Trasandina Web", Trasandina, sans-serif',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            Proceso {name}
          </Typography>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, textAlign: 'right', color: '#666' }}>
              {isActive && progress === 0 ? 1 : Math.round(progress)}%
            </Typography>
            <BatchProgress 
              name={name}
              progress={progress}
              isActive={isActive}
              startTime={startTime}
              batchName={batchName}
              statistics={statistics}
              obtainBirthDates={lastJobObtainBirthDates}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Checkbox para BPS */}
            {isBPS && (
              <Box sx={{ mb: 1 }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: '#1B285E',
                  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={obtainBirthDates}
                    onChange={(e) => setObtainBirthDates(e.target.checked)}
                    style={{ marginRight: '6px', transform: 'scale(0.9)' }}
                    disabled={isActive}
                  />
                  Obtener fechas de nacimiento para todos los prospectos afiliables
                </label>
              </Box>
            )}
            
            <Button 
              variant="contained"
              size="small"
              sx={{
                backgroundColor: isActive ? '#ccc' : '#1B285E',
                color: '#fff',
                '&:hover': {
                  backgroundColor: isActive ? '#ccc' : '#2a3b7c'
                },
                textTransform: 'none',
                fontFamily: '"Trasandina Web", Trasandina, sans-serif',
                fontSize: '0.875rem',
                py: 1,
                minWidth: '120px'
              }}
              onClick={onStart}
              disabled={isActive}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Iniciar Proceso'
              )}
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{
                borderColor: isActive ? '#d32f2f' : '#ccc',
                color: isActive ? '#d32f2f' : '#ccc',
                '&:hover': {
                  borderColor: isActive ? '#b71c1c' : '#ccc',
                  backgroundColor: 'transparent'
                },
                textTransform: 'none',
                fontFamily: '"Trasandina Web", Trasandina, sans-serif',
                fontSize: '0.875rem',
                py: 1
              }}
              onClick={onStop}
              disabled={!isActive && progress !== 100}
            >
              {progress === 100 && isActive ? 'Reiniciar' : 'Detener Proceso'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}, (prevProps, nextProps) => {
  return prevProps.progress === nextProps.progress &&
         prevProps.isActive === nextProps.isActive &&
         prevProps.startTime === nextProps.startTime &&
         prevProps.batchName === nextProps.batchName &&
         prevProps.loading === nextProps.loading &&
         prevProps.obtainBirthDates === nextProps.obtainBirthDates &&
         prevProps.lastJobObtainBirthDates === nextProps.lastJobObtainBirthDates &&
         JSON.stringify(prevProps.statistics) === JSON.stringify(nextProps.statistics);
});

const BatchJobs = () => {
  const [apolloStatus, setApolloStatus] = useState({
    progress: 0,
    isActive: false,
    statistics: null,
    startTime: null,
    obtainBirthDates: null
  });
  const [bpsStatus, setBpsStatus] = useState({
    progress: 0,
    isActive: false,
    statistics: null,
    startTime: null,
    obtainBirthDates: null
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    details: null
  });

  const [loadingProcess, setLoadingProcess] = useState({
    apollo: false,
    bps: false
  });

  const [obtainBirthDates, setObtainBirthDates] = useState(false);

  const parseStatistics = useCallback((statistics) => {
    if (!statistics) return null;
    try {
      console.log('Raw statistics:', statistics);

      // Si ya es un objeto, retornarlo
      if (typeof statistics === 'object' && !Array.isArray(statistics)) {
        return statistics;
      }

      // Intentar parsear la cadena una vez
      let parsed = typeof statistics === 'string' ? JSON.parse(statistics) : statistics;
      console.log('First parse:', parsed);

      // Si sigue siendo string, intentar parsear de nuevo
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
        console.log('Second parse:', parsed);
      }

      // Si aún es string, puede tener caracteres unicode escapados
      if (typeof parsed === 'string') {
        // Decodificar caracteres unicode
        const decoded = parsed.replace(/\\u([\d\w]{4})/gi, (match, grp) =>
          String.fromCharCode(parseInt(grp, 16))
        );
        parsed = JSON.parse(decoded);
        console.log('After unicode decode:', parsed);
      }

      // Asegurar que tenemos un objeto con las propiedades correctas
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Failed to parse statistics into an object');
      }

      // Convertir claves a camelCase y valores a números si es necesario
      const result = {};
      for (const [key, value] of Object.entries(parsed)) {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        result[camelKey] = typeof value === 'string' ? parseInt(value, 10) : value;
      }

      console.log('Final parsed statistics:', result);
      return result;
    } catch (e) {
      console.error('Error parsing statistics:', e, '\nRaw statistics:', statistics);
      return null;
    }
  }, []);

  const fetchInitialStatus = async (processName) => {
    try {
      const response = await apiService.get(`/api/batchjob/status/${processName}`);
      const jobKey = processName.toLowerCase();
      
      // Update loading state based on isActive
      setLoadingProcess(prev => ({
        ...prev,
        [jobKey]: response.isActive
      }));

      if (processName === 'Apollo') {
        setApolloStatus({
          ...response,
          statistics: parseStatistics(response.statistics),
          obtainBirthDates: response.obtainBirthDates
        });
      } else if (processName === 'BPS') {
        setBpsStatus({
          ...response,
          statistics: parseStatistics(response.statistics),
          obtainBirthDates: response.obtainBirthDates
        });
      }
    } catch (error) {
      console.error(`Error fetching ${processName} status:`, error);
    }
  };

  // Polling mechanism to update status
  useEffect(() => {
    let isMounted = true;
    const pollInterval = 5000; // Poll every 5 seconds

    const pollStatus = async () => {
      if (!isMounted) return;

      await fetchInitialStatus('Apollo');
      await fetchInitialStatus('BPS');
    };

    // Initial fetch
    pollStatus();

    // Set up polling
    const intervalId = setInterval(pollStatus, pollInterval);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const stopBatchJob = async (jobName) => {
    const jobKey = jobName.toLowerCase();
    try {
      let response;
      if (jobKey === 'apollo') {
        response = await batchService.stopApolloJob();
      } else if (jobKey === 'bps') {
        response = await batchService.stopBPSJob();
      } else {
        throw new Error(`Unknown job name: ${jobName}`);
      }

      if (!response) {
        throw new Error(`Failed to stop ${jobName} job`);
      }

      // Si está al 100%, se resetea
      if (jobKey === 'apollo' && apolloStatus.progress === 100) {
        setApolloStatus({
          progress: 0,
          isActive: false,
          statistics: null,
          startTime: null,
          obtainBirthDates: null
        });
      } else if (jobKey === 'bps' && bpsStatus.progress === 100) {
        setBpsStatus({
          progress: 0,
          isActive: false,
          statistics: null,
          startTime: null,
          obtainBirthDates: null
        });
      } else {
        // Si no está al 100%, forzamos un fetch puntual para refrescar
        await fetchInitialStatus(jobName);
      }

      setSnackbar({
        open: true,
        message: jobKey === 'apollo' && apolloStatus.progress === 100
          ? 'Proceso Apollo reiniciado exitosamente'
          : jobKey === 'bps' && bpsStatus.progress === 100
          ? 'Proceso BPS reiniciado exitosamente'
          : `Proceso ${jobName} detenido exitosamente`,
        severity: 'success',
        details: null
      });
    } catch (error) {
      console.error(`Error stopping ${jobName} job:`, error);
      setSnackbar({
        open: true,
        message: `Error stopping ${jobName} job: ${error.message}`,
        severity: 'error',
        details: error.stack
      });
    }
  };

  const triggerBatchJob = async (processType) => {
    try {
      setLoadingProcess(prev => ({ ...prev, [processType.toLowerCase()]: true }));
      
      let response;
      if (processType === 'Apollo') {
        response = await batchService.triggerApolloJob();
      } else if (processType === 'BPS') {
        response = await batchService.triggerBPSJob(obtainBirthDates);
      }

      if (!response) {
        throw new Error(`Failed to trigger ${processType} job`);
      }

      // Mostrar mensaje del backend (o uno por defecto)
      setSnackbar({
        open: true,
        message: response.message || `Successfully triggered ${processType} job`,
        severity: response.message?.toLowerCase().includes('no hay') ? 'info' : 'success',
        details: null
      });

      // Fetch puntual para actualizar estado
      await fetchInitialStatus(processType);
    } catch (error) {
      console.error(`Error triggering ${processType} job:`, error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Failed to trigger ${processType} job: ${error.message}`,
        severity: 'error',
        details: error.stack
      });
    } finally {
      setLoadingProcess(prev => ({ ...prev, [processType.toLowerCase()]: false }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          color: '#1B285E',
          fontFamily: '"Trasandina Web", Trasandina, sans-serif',
          fontWeight: 600
        }}
      >
        Estado de Procesos Batch
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          {/* Apollo API Rate Limit Information */}
          {/* <RateLimitInfo /> */}
          
          <BatchJobCard
            name="Apollo"
            progress={apolloStatus.progress}
            isActive={apolloStatus.isActive}
            startTime={apolloStatus.startTime}
            batchName={apolloStatus.batchName}
            statistics={apolloStatus.statistics}
            onStart={() => triggerBatchJob('Apollo')}
            onStop={() => stopBatchJob('Apollo')}
            loading={loadingProcess.apollo}
            obtainBirthDates={false}
            setObtainBirthDates={() => {}}
            lastJobObtainBirthDates={apolloStatus.obtainBirthDates}
          />

          <BatchJobCard
            name="BPS"
            progress={bpsStatus.progress}
            isActive={bpsStatus.isActive}
            startTime={bpsStatus.startTime}
            batchName={bpsStatus.batchName}
            statistics={bpsStatus.statistics}
            onStart={() => triggerBatchJob('BPS')}
            onStop={() => stopBatchJob('BPS')}
            loading={loadingProcess.bps}
            obtainBirthDates={obtainBirthDates}
            setObtainBirthDates={setObtainBirthDates}
            lastJobObtainBirthDates={bpsStatus.obtainBirthDates}
          />
        </Grid>
      </Grid>

      {snackbar.severity === 'error' ? (
        <ErrorToast
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          details={snackbar.details}
          autoHideDuration={10000}
        />
      ) : (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ 
              width: '100%',
              fontFamily: '"Trasandina Web", Trasandina, sans-serif'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
};

export default memo(BatchJobs);
